import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Search, Sparkles, Tag, Flame, Clock, Gift, Star, TrendingUp, Zap,
  Sword, Wand2, Compass, Brain, Rocket, Trophy, Car, Ghost, Globe2, Tent, Users, Crosshair, Puzzle, Coffee, Baby,
  Store, ArrowRight, Play, ShieldCheck, Heart, DollarSign, PiggyBank,
} from "lucide-react";
import { api, type Game, type Genre, type Shop } from "@/lib/api";
import { GameCard } from "@/components/game-card";
import { EmptyState, ErrorState, LoadingGrid } from "@/components/empty-state";
import { useSettings } from "@/lib/settings";
import { ShopLogo } from "@/components/shop-logo";
import { HorizontalGameCarousel } from "@/components/horizontal-game-carousel";
import { useMemo, useState, type ComponentType } from "react";
import { Keys4SteamLanding } from "@/components/keys4steam-landing";
import { HomeOfferPreferences } from "@/components/offer-preferences-dialog";
import { getSiteBrand } from "@/lib/site-brand.functions";

export const Route = createFileRoute("/_site/")({
  loader: () => getSiteBrand(),
  head: ({ loaderData }) => loaderData === "keys4steam" ? ({
    meta: [
      { title: "Keys4Steam — Compare Steam Key Prices" },
      { name: "description", content: "Compare Steam key prices and deals from multiple stores. Find top Steam games and upcoming releases, powered by NexKey.gg." },
      { property: "og:title", content: "Keys4Steam — Steam Keys. Better Prices." },
      { property: "og:description", content: "Compare Steam offers from multiple stores and find the best price, powered by NexKey.gg." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://keys4steam.com/" },
      { property: "og:site_name", content: "Keys4Steam" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://keys4steam.com/" }],
  }) : ({
    meta: [
      { title: "NexKey.gg — Compare Game Prices Across Trusted Stores" },
      { name: "description", content: "Compare game key prices across official stores and trusted marketplaces. Real-time price comparison, daily updates, biggest discounts and new releases in one place." },
      { property: "og:title", content: "NexKey.gg — Compare Game Prices Across Trusted Stores" },
      { property: "og:description", content: "One search, every price. Compare official stores and trusted marketplaces side by side." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "/" }],
  }),
  component: Home,
});

/* ---------------- Types & helpers ---------------- */

const POPULAR_SEARCHES = ["Cyberpunk", "Elden Ring", "Baldur's Gate", "Hogwarts Legacy", "Starfield", "GTA V"];

function Home() {
  const brand = Route.useLoaderData();
  if (brand === "keys4steam") return <Keys4SteamLanding />;
  return <NexKeyHome />;
}

function NexKeyHome() {
  // Popularity-first queries. Each section filters first, then ranks by popularity.
  const popular = useQuery({ queryKey: ["h", "popular"], queryFn: () => api.games({ sort: "popular", limit: 14 }) });
  const bestDeals = useQuery({ queryKey: ["h", "bestDeals"], queryFn: () => api.games({ sort: "popular", discounted: true, limit: 14 }) });
  const comingSoon = useQuery({ queryKey: ["h", "soon2"], queryFn: () => api.games({ sort: "popular", upcoming: true, limit: 14 }) });
  const under5 = useQuery({ queryKey: ["h", "under5v2"], queryFn: () => api.games({ sort: "popular", maxPrice: 500, limit: 14 }) });
  const under10 = useQuery({ queryKey: ["h", "under10v2"], queryFn: () => api.games({ sort: "popular", maxPrice: 1000, limit: 14 }) });
  const genres = useQuery({ queryKey: ["h", "genres"], queryFn: () => api.genres() });
  const shops = useQuery({ queryKey: ["h", "shops"], queryFn: () => api.shops() });
  const stats = useQuery({ queryKey: ["h", "stats"], queryFn: () => api.stats() });

  return (
    <div>
      <Hero stats={stats.data?.data ?? null} />
      <HomeOfferPreferences shops={shops.data?.data ?? []} />

      <Row title="Most Popular" subtitle="Community favorites right now" icon={<Star className="h-4 w-4" />} href="/games?sort=popular" q={popular} />
      <Row title="Best Deals" subtitle="Top games with the biggest price cuts" icon={<Tag className="h-4 w-4" />} href="/games?sort=discount" q={bestDeals} />
      <Row title="Coming Soon" subtitle="Most anticipated upcoming games" icon={<Clock className="h-4 w-4" />} href="/games?release=upcoming" q={comingSoon} />
      <Row title="Under €5" subtitle="Popular picks for less than a coffee" icon={<PiggyBank className="h-4 w-4" />} href="/games?maxPrice=5" q={under5} />
      <Row title="Under €10" subtitle="Wallet-friendly bestsellers" icon={<DollarSign className="h-4 w-4" />} href="/games?maxPrice=10" q={under10} />

      <GenreSection genres={genres.data?.data ?? []} loading={genres.isLoading} />
      <ShopSection shops={shops.data?.data ?? []} loading={shops.isLoading} />

    </div>
  );
}


/* ---------------- Hero ---------------- */

function Hero({ stats }: { stats: { games: number; shops: number; offers: number; genres: number } | null }) {
  const { t } = useSettings();
  const [q, setQ] = useState("");
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const query = q.trim();
    if (!query) return;
    try {
      const r = await api.search(query);
      const norm = query.toLowerCase();
      const exact = r.data.find((g) => g.title.toLowerCase() === norm);
      if (exact) {
        window.location.href = `/game/${exact.slug}`;
        return;
      }
    } catch { /* fall through */ }
    window.location.href = `/games?q=${encodeURIComponent(query)}`;
  };

  const titleA = t("hero.title.a");
  const titleB = t("hero.title.b");
  const titleC = t("hero.title.c");
  const gameCount = stats?.games ?? 0;
  const searchPlaceholder = t("hero.searchPlaceholder").replace("{count}", gameCount > 0 ? gameCount.toLocaleString() : "100,000");

  return (
    <section className="relative isolate overflow-hidden">
      {/* Premium ambient background — no grid, subtle brand-red glows only */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        {/* Deep base wash */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_80%_at_50%_-10%,color-mix(in_oklab,var(--brand-deep)_60%,transparent),transparent_60%)]" />
        {/* Strong off-center brand bloom */}
        <div className="absolute -top-40 left-1/2 h-[520px] w-[900px] -translate-x-1/2 rounded-[50%] bg-[radial-gradient(closest-side,color-mix(in_oklab,var(--primary)_32%,transparent),transparent_75%)] blur-3xl" />
        {/* Secondary deep-red ember to the right */}
        <div className="absolute right-[-10%] top-[30%] h-[420px] w-[520px] rounded-full bg-[radial-gradient(closest-side,color-mix(in_oklab,var(--brand-deep)_24%,transparent),transparent_70%)] blur-3xl" />
        {/* Tertiary dark-red depth near lower-center */}
        <div className="absolute left-1/2 top-[60%] h-[300px] w-[720px] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,color-mix(in_oklab,var(--brand-deep)_18%,transparent),transparent_75%)] blur-3xl" />
        {/* Cool counter-tone to break the monochrome */}
        <div className="absolute -left-24 bottom-[-15%] h-[380px] w-[520px] rounded-full bg-[radial-gradient(closest-side,oklch(0.55_0.15_260/0.16),transparent_70%)] blur-3xl" />
        {/* Diagonal light streak */}
        <div className="absolute inset-x-0 top-1/3 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
        {/* Soft vignette for premium framing */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_90%_70%_at_50%_50%,transparent_55%,oklch(0_0_0/0.35)_100%)]" />
        {/* Ultra-faint dot field (replaces the grid) */}
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage: "radial-gradient(oklch(0.85 0.02 60) 1px, transparent 1px)",
            backgroundSize: "26px 26px",
            maskImage: "radial-gradient(ellipse 70% 55% at 50% 40%, black 30%, transparent 75%)",
            WebkitMaskImage: "radial-gradient(ellipse 70% 55% at 50% 40%, black 30%, transparent 75%)",
          }}
        />
        {/* Bottom fade into page */}
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-background" />

      </div>

      <div className="container mx-auto px-4 pt-6 pb-8 md:pt-8 md:pb-10">
        <div className="mx-auto max-w-4xl text-center">
          {/* Badge */}
          <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/[0.08] px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-primary shadow-glow backdrop-blur">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/70 opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
            </span>
            {t("hero.badge")}
          </span>

          {/* Headline */}
          <h1 className="mt-4 font-display text-4xl font-black leading-[1.02] tracking-tight md:text-5xl lg:text-6xl">
            <span className="block text-foreground">{titleA}</span>
            <span className="mt-1 block bg-[linear-gradient(100deg,var(--brand-bright)_0%,var(--primary)_45%,var(--brand-deep)_100%)] bg-clip-text text-transparent [text-shadow:0_0_60px_color-mix(in_oklab,var(--primary)_25%,transparent)]">
              {titleB}{titleC ? ` ${titleC}` : ""}
            </span>
          </h1>

          {/* Search */}
          <form
            onSubmit={onSubmit}
            className="group relative mx-auto mt-6 flex max-w-2xl items-center gap-2 rounded-full border border-border/70 bg-background/60 p-1.5 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.7),inset_0_1px_0_oklch(1_0_0/0.04)] backdrop-blur-xl transition focus-within:border-primary/60 focus-within:shadow-glow"
          >
            <span className="absolute inset-0 -z-10 rounded-full bg-[radial-gradient(60%_120%_at_50%_50%,color-mix(in_oklab,var(--primary)_10%,transparent),transparent_70%)] opacity-0 transition group-focus-within:opacity-100" />
            <Search className="ml-3 h-5 w-5 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={searchPlaceholder}
              className="flex-1 bg-transparent px-2 py-2.5 text-sm outline-none placeholder:text-muted-foreground/60 md:text-[15px]"
            />
            <button className="rounded-full bg-primary-gradient px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-glow transition hover:brightness-110">
              {t("hero.searchBtn")}
            </button>
          </form>


          {/* Popular */}
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-xs text-muted-foreground">
            <span className="text-muted-foreground/80">{t("hero.popular")}</span>
            {POPULAR_SEARCHES.map((s) => (
              <Link
                key={s}
                to="/games"
                search={{ q: s } as any}
                className="rounded-full border border-border/60 bg-surface/50 px-3 py-1 backdrop-blur transition hover:border-primary/60 hover:text-primary"
              >
                {s}
              </Link>
            ))}
          </div>

          {/* Stats */}
          {stats && (
            <div className="mx-auto mt-8 grid max-w-2xl grid-cols-3 gap-3 md:gap-4">
              <StatPill label={t("hero.stat.games")} value={stats.games} />
              <StatPill label={t("hero.stat.stores")} value={stats.shops} />
              <StatPill label={t("hero.stat.offers")} value={stats.offers} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function StatPill({ label, value }: { label: string; value: number }) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-border/70 bg-surface/40 py-3.5 backdrop-blur-xl transition hover:border-primary/40">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
      <div className="font-display text-2xl font-black text-primary md:text-3xl">{value.toLocaleString()}</div>
      <div className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
    </div>
  );
}

/* ---------------- Featured game ---------------- */

function FeaturedGame({ game, loading }: { game: Game | null; loading: boolean }) {
  const { formatPrice } = useSettings();
  if (loading) {
    return (
      <section className="container mx-auto px-4 py-8">
        <div className="h-[360px] animate-pulse rounded-2xl border border-border bg-surface/50" />
      </section>
    );
  }
  if (!game) return null;
  const bg = game.backgroundImage || game.headerImage;
  const price = game.bestOffer ?? game.steamPriceFinal;
  const savings = game.savingsPercent ?? game.steamDiscountPercent ?? null;
  const trailer = game.movies?.[0]?.webm480 || game.movies?.[0]?.mp4_480 || null;

  return (
    <section className="container mx-auto px-4 py-10">
      <div className="mb-4 flex items-center gap-2">
        <span className="grid h-8 w-8 place-items-center rounded-md bg-primary/15 text-primary">
          <Zap className="h-4 w-4" />
        </span>
        <h2 className="font-display text-2xl font-bold md:text-3xl">Featured Game</h2>
      </div>
      <Link
        to="/game/$slug"
        params={{ slug: game.slug }}
        className="group relative block overflow-hidden rounded-2xl border border-border bg-card"
      >
        <div className="absolute inset-0">
          {bg && <img src={bg} alt="" className="h-full w-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/40 to-transparent" />
        </div>
        <div className="relative grid gap-6 p-6 md:grid-cols-2 md:p-10 lg:p-14">
          <div>
            <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-[11px] font-semibold text-primary">
              <Star className="h-3 w-3" /> Editor's Pick
            </span>
            <h3 className="mt-4 font-display text-3xl font-black leading-tight md:text-5xl">{game.title}</h3>
            {game.shortDescription && (
              <p className="mt-3 max-w-xl text-sm text-muted-foreground md:text-base">{game.shortDescription}</p>
            )}
            <div className="mt-5 flex flex-wrap gap-4 text-xs text-muted-foreground">
              {game.developer && <span><span className="text-foreground/60">Developer:</span> <strong className="text-foreground">{game.developer}</strong></span>}
              {game.releaseDate && <span><span className="text-foreground/60">Released:</span> <strong className="text-foreground">{new Date(game.releaseDate).toLocaleDateString()}</strong></span>}
              {game.metacriticScore != null && <span><span className="text-foreground/60">Metacritic:</span> <strong className="text-primary">{game.metacriticScore}</strong></span>}
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              {price != null && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Lowest price</div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-display text-3xl font-black text-primary md:text-4xl">
                      {price === 0 ? "Free" : formatPrice(price, game.steamCurrency)}
                    </span>
                    {savings != null && savings > 0 && (
                      <span className="rounded bg-success px-2 py-0.5 text-xs font-bold text-success-foreground">-{savings}%</span>
                    )}
                  </div>
                </div>
              )}
              <span className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-glow transition group-hover:brightness-110">
                Compare Prices <ArrowRight className="h-4 w-4" />
              </span>
            </div>
          </div>
          <div className="relative hidden aspect-video overflow-hidden rounded-xl border border-border/60 shadow-2xl md:block">
            {trailer ? (
              <video src={trailer} muted loop autoPlay playsInline poster={game.movies?.[0]?.thumbnail ?? undefined} className="h-full w-full object-cover" />
            ) : game.headerImage ? (
              <>
                <img src={game.headerImage} alt="" className="h-full w-full object-cover" />
                <div className="absolute inset-0 grid place-items-center bg-background/30">
                  <span className="grid h-14 w-14 place-items-center rounded-full bg-primary text-primary-foreground shadow-glow">
                    <Play className="h-6 w-6" />
                  </span>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </Link>
    </section>
  );
}

/* ---------------- Row ---------------- */

type RowQuery = { data?: { data: Game[] } | undefined; isLoading: boolean; isError: boolean; error: unknown };

function Row({
  title, subtitle, icon, href, q,
}: { title: string; subtitle?: string; icon: React.ReactNode; href?: string; q: RowQuery }) {
  const games = q.data?.data ?? [];
  if (q.isLoading) {
    return (
      <Section title={title} subtitle={subtitle} icon={icon} href={href}>
        <LoadingGrid count={4} />
      </Section>
    );
  }
  if (q.isError) {
    return (
      <Section title={title} subtitle={subtitle} icon={icon} href={href}>
        <ErrorState message={(q.error as Error).message} />
      </Section>
    );
  }
  if (games.length === 0) return null; // hide empty per spec
  return (
    <Section title={title} subtitle={subtitle} icon={icon} href={href}>
      <HScroll items={games} />
    </Section>
  );
}

function Section({
  title, subtitle, icon, href, children,
}: { title: string; subtitle?: string; icon?: React.ReactNode; href?: string; children: React.ReactNode }) {
  return (
    <section className="container mx-auto px-4 py-8">
      <div className="mb-5 flex items-end justify-between gap-4">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 font-display text-xl font-bold md:text-2xl">
            {icon && <span className="grid h-8 w-8 place-items-center rounded-md bg-primary/15 text-primary">{icon}</span>}
            <span className="truncate">{title}</span>
          </h2>
          {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
        </div>
        {href && (
          <a href={href} className="shrink-0 text-xs font-semibold text-muted-foreground hover:text-primary">
            View all →
          </a>
        )}
      </div>
      {children}
    </section>
  );
}

function HScroll({ items }: { items: Game[] }) {
  return (
    <HorizontalGameCarousel>
      {items.map((g) => (
        <div key={g.id} className="w-[75%] shrink-0 snap-start sm:w-[45%] md:w-[calc((100%_-_2rem)/3)] lg:w-[calc((100%_-_3rem)/4)]">
          <GameCard game={g} className="h-[19rem] sm:h-[19.5rem]" />
        </div>
      ))}
    </HorizontalGameCarousel>
  );
}

/* ---------------- Genres ---------------- */

const GENRE_ICONS: Record<string, ComponentType<{ className?: string }>> = {
  action: Sword, rpg: Wand2, "role-playing-games-rpg": Wand2, adventure: Compass,
  strategy: Brain, simulation: Rocket, sports: Trophy, racing: Car, horror: Ghost,
  "open-world": Globe2, survival: Tent, mmo: Users, mmorpg: Users, fps: Crosshair,
  indie: Sparkles, casual: Coffee, puzzle: Puzzle, "free-to-play": Gift,
  "massively-multiplayer": Users, "family-friendly": Baby,
};

// Only true gaming genres on the homepage. Steam mixes in software/tooling
// categories (Accounting, Animation & Modeling, Utilities, Audio/Video
// Production, Software Training, Education, Design & Illustration, Web
// Publishing, Photo Editing) that gamers never browse — hide them here.
const GAMING_GENRE_SLUGS = new Set([
  "action", "adventure", "rpg", "role-playing-games-rpg", "strategy",
  "simulation", "sports", "racing", "indie", "casual", "puzzle",
  "massively-multiplayer", "mmo", "mmorpg", "fps", "shooter",
  "free-to-play", "early-access", "horror", "survival", "open-world",
  "co-op", "multiplayer", "singleplayer", "fighting", "platformer",
  "roguelike", "rogue-like", "sandbox", "stealth", "tactical",
  "family-friendly", "arcade", "board-games", "card-games",
]);

function GenreSection({ genres, loading }: { genres: Genre[]; loading: boolean }) {
  const gaming = genres.filter((g) => GAMING_GENRE_SLUGS.has(g.slug.toLowerCase()));
  const withCounts = gaming.filter((g) => (g.count ?? 0) > 0);
  const list = withCounts.length > 0 ? withCounts : gaming;
  const shown = list.slice(0, 14);
  if (loading) {
    return (
      <Section title="Browse by Genre" subtitle="Jump straight into your favorite category" icon={<Sparkles className="h-4 w-4" />} href="/genres">
        <HorizontalGameCarousel>
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="h-32 w-[48%] shrink-0 snap-start animate-pulse rounded-xl border border-border bg-surface/40 sm:w-[30%] md:w-[22%] lg:w-[calc((100%_-_5rem)/6)]" />
          ))}
        </HorizontalGameCarousel>
      </Section>
    );
  }
  if (shown.length === 0) return null;
  return (
    <Section title="Browse by Genre" subtitle="Jump straight into your favorite category" icon={<Sparkles className="h-4 w-4" />} href="/genres">
      <HorizontalGameCarousel>
        {shown.map((g) => {
          const Icon = GENRE_ICONS[g.slug.toLowerCase()] ?? GENRE_ICONS[g.name.toLowerCase()] ?? Sparkles;
          return (
            <Link
              key={g.id}
              to="/games"
              search={{ genre: g.slug } as any}
              className="group relative flex h-32 w-[48%] shrink-0 snap-start flex-col items-center justify-center gap-2 overflow-hidden rounded-xl border border-border bg-card p-4 text-center transition hover:-translate-y-0.5 hover:border-primary hover:shadow-glow sm:w-[30%] md:w-[22%] lg:w-[calc((100%_-_5rem)/6)]"
            >
              <span className="grid h-11 w-11 place-items-center rounded-lg bg-primary/15 text-primary transition group-hover:bg-primary group-hover:text-primary-foreground">
                <Icon className="h-5.5 w-5.5" />
              </span>
              <span className="font-display text-sm font-semibold">{g.name}</span>
              {g.count != null && g.count > 0 && (
                <span className="text-[10px] text-muted-foreground">{g.count} {g.count === 1 ? "game" : "games"}</span>
              )}
            </Link>
          );
        })}
      </HorizontalGameCarousel>
    </Section>
  );
}

/* ---------------- Platforms ---------------- */

const PLATFORMS: { name: string; q: string; letter: string; tint: string }[] = [
  { name: "Steam",             q: "Steam",             letter: "S", tint: "from-[oklch(0.35_0.06_240)] to-[oklch(0.22_0.04_240)]" },
  { name: "Epic Games",        q: "Epic",              letter: "E", tint: "from-[oklch(0.30_0.02_260)] to-[oklch(0.18_0.02_260)]" },
  { name: "EA App",            q: "EA",                letter: "E", tint: "from-[oklch(0.30_0.08_265)] to-[oklch(0.18_0.04_265)]" },
  { name: "Ubisoft Connect",   q: "Ubisoft",           letter: "U", tint: "from-[oklch(0.35_0.06_235)] to-[oklch(0.20_0.04_235)]" },
  { name: "GOG",               q: "GOG",               letter: "G", tint: "from-[oklch(0.40_0.16_305)] to-[oklch(0.22_0.08_305)]" },
  { name: "Battle.net",        q: "Battle.net",        letter: "B", tint: "from-[oklch(0.35_0.10_240)] to-[oklch(0.20_0.06_240)]" },
  { name: "Xbox",              q: "Xbox",              letter: "X", tint: "from-[oklch(0.35_0.12_150)] to-[oklch(0.22_0.08_150)]" },
  { name: "PlayStation",       q: "PlayStation",       letter: "P", tint: "from-[oklch(0.30_0.10_260)] to-[oklch(0.20_0.06_260)]" },
  { name: "Nintendo",          q: "Nintendo",          letter: "N", tint: "from-[oklch(0.40_0.18_25)] to-[oklch(0.25_0.10_25)]" },
];

function PlatformSection() {
  return (
    <Section title="Browse by Platform" subtitle="Find keys for every store and console" icon={<Store className="h-4 w-4" />}>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {PLATFORMS.map((p) => (
          <Link
            key={p.name}
            to="/games"
            search={{ q: p.q } as any}
            className={`group relative flex items-center gap-3 overflow-hidden rounded-xl border border-border bg-gradient-to-br ${p.tint} p-4 transition hover:-translate-y-0.5 hover:border-primary hover:shadow-glow`}
          >
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-background/40 font-display text-lg font-black text-foreground backdrop-blur-sm">
              {p.letter}
            </span>
            <div className="min-w-0">
              <div className="truncate font-display text-sm font-semibold">{p.name}</div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Browse keys</div>
            </div>
          </Link>
        ))}
      </div>
    </Section>
  );
}

/* ---------------- Shops ---------------- */

function ShopStrip({ shops, loading }: { shops: any[]; loading: boolean }) {
  const active = shops.filter((s) => (s.offerCount ?? 0) > 0 || s.isActive);
  if (loading || active.length === 0) return null;
  return (
    <section className="container mx-auto px-4 py-6">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Trusted partner stores
        </span>
        <Link to="/shops" className="text-xs font-semibold text-muted-foreground hover:text-primary">All stores →</Link>
      </div>
      <div className="mt-3 -mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {active.slice(0, 20).map((s) => (
          <Link
            key={s.id}
            to="/shops"
            className="flex shrink-0 snap-start items-center gap-2 rounded-lg border border-border/60 bg-card/60 px-3 py-2 text-xs transition hover:border-primary hover:text-primary"
          >
            <ShopLogo shop={s} size="xs" />
            <span className="font-medium">{s.name}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

type HomepageShop = Shop & { offerCount?: number | null };

function ShopSection({ shops, loading }: { shops: HomepageShop[]; loading: boolean }) {
  const active = shops.filter((s) => (s.offerCount ?? 0) > 0 || s.isActive);
  if (loading) {
    return (
      <Section title="Partner Stores" subtitle="Compare prices from our trusted stores" icon={<ShieldCheck className="h-4 w-4" />} href="/shops">
        <HorizontalGameCarousel>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-28 w-[56%] shrink-0 snap-start animate-pulse rounded-xl border border-border bg-surface/40 sm:w-[34%] md:w-[24%] lg:w-[calc((100%_-_4rem)/5)]" />
          ))}
        </HorizontalGameCarousel>
      </Section>
    );
  }
  if (active.length === 0) return null;
  return (
    <Section title="Partner Stores" subtitle="Compare prices from our trusted stores" icon={<ShieldCheck className="h-4 w-4" />} href="/shops">
      <HorizontalGameCarousel>
        {active.slice(0, 20).map((s) => (
          <Link
            key={s.id}
            to="/shops"
            className="group flex h-28 w-[56%] shrink-0 snap-start flex-col items-center justify-center gap-2 rounded-xl border border-border bg-card px-4 py-3 text-center transition hover:-translate-y-0.5 hover:border-primary hover:shadow-glow sm:w-[34%] md:w-[24%] lg:w-[calc((100%_-_4rem)/5)]"
          >
            <ShopLogo shop={s} size="lg" />
            <div className="font-display text-sm font-semibold">{s.name}</div>
          </Link>
        ))}
      </HorizontalGameCarousel>
    </Section>
  );
}

/* ---------------- Collections (auto from genres) ---------------- */

const COLLECTION_KEYWORDS = [
  "Open World", "Souls-like", "FPS", "Co-op", "Strategy", "Survival", "Horror",
  "Fantasy", "Sci-Fi", "Racing", "Steam Deck", "Controller Support", "VR",
  "Family Friendly", "Indie", "Multiplayer", "Puzzle", "Rogue-like",
];

function CollectionsSection({ genres }: { genres: Genre[] }) {
  // Automatically surface which curated keywords match an actual genre in DB.
  const map = new Map<string, Genre>();
  genres.forEach((g) => map.set(g.name.toLowerCase(), g));
  const matched = COLLECTION_KEYWORDS
    .map((label) => ({ label, genre: map.get(label.toLowerCase()) }))
    .filter((c) => c.genre && (c.genre.count ?? 0) > 0);

  if (matched.length === 0) return null;

  return (
    <Section title="Collections" subtitle="Curated selections generated from your library" icon={<Heart className="h-4 w-4" />}>
      <div className="flex flex-wrap gap-2">
        {matched.map(({ label, genre }) => (
          <Link
            key={label}
            to="/games"
            search={{ genre: genre!.slug } as any}
            className="group inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm transition hover:-translate-y-0.5 hover:border-primary hover:text-primary"
          >
            <span className="font-medium">{label}</span>
            <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-bold text-primary">{genre!.count}</span>
          </Link>
        ))}
      </div>
    </Section>
  );
}

function HonestSection() {
  const { t } = useSettings();
  return (
    <section className="container mx-auto px-4 pb-16 pt-8">
      <div className="rounded-2xl border border-border bg-surface/60 p-6 md:p-10">
        <h2 className="font-display text-2xl font-bold">{t("home.honest.title")}</h2>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">
          {t("home.honest.body")}{" "}
          <Link to="/legal/steam" className="text-primary underline underline-offset-4">
            {t("home.honest.link")}
          </Link>
          .
        </p>
      </div>
    </section>
  );
}
